import React from 'react'
import ReactDOM from 'react-dom'
import * as Sentry from '@sentry/react'
import {BrowserTracing} from '@sentry/tracing'
import {App} from './App'
import {AppProviders} from './context'
import {asyncWithLDProvider} from 'launchdarkly-react-client-sdk'
import reportWebVitals from './reportWebVitals'
import {
  ErrorContainer,
  TextContainer,
  ImageContainer,
  ItemContainer,
  ScreenContainer,
} from './components/ui/error-fallback'
import {thryvGuyConfused} from './assets'
import {ParagraphText, Button} from '@thryvlabs/maverick'
import './bootstrap'
import {Provider} from 'react-redux'
import Store from './app-store/appStore'
const environment =
  process.env.REACT_APP_DATADOG_ENV || process.env.REACT_APP_ENV

// Lazy so swagger-ui-react stays out of the main bundle.
const ApiDocs = React.lazy(() => import('./screens/api-docs'))

;(async () => {
  // Standalone API docs: render Swagger UI on its own, outside the app shell
  // and outside Auth0, so it looks like a plain Swagger page and needs no Okta
  // login. Skip the whole provider/auth tree.
  if (window.location.pathname.replace(/\/+$/, '') === '/api-docs') {
    ReactDOM.render(
      <React.Suspense
        fallback={<div style={{padding: 24}}>Loading API docs…</div>}
      >
        <ApiDocs />
      </React.Suspense>,
      document.getElementById('root'),
    )
    return
  }

  const LDProvider = await asyncWithLDProvider({
    clientSideID: process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID,
  })

  Sentry.init({
    integrations: [new BrowserTracing()],
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: environment,
    beforeSend(event) {
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames || []
      if (frames.some(f => f?.filename?.includes('@thryvlabs/maverick'))) {
        return null
      }
      const errorType = event?.exception?.values?.[0]?.type || ''
      const errorMsg = event?.exception?.values?.[0]?.value || ''
      const args = event?.extra?.arguments
      const isMaverickRadio =
        errorMsg === 'r is not a function' &&
        Array.isArray(args) &&
        args.some(a => a?.target?.includes('[type="radio"]'))
      if (isMaverickRadio) {
        return null
      }
      // Drop transient browser network failures — not actionable app bugs.
      // NOTE: we deliberately do NOT filter "Load failed" (Safari's fetch
      // failure signature) — that surfaces load-balancer/backend outages
      // that must remain visible in Sentry.
      if (
        errorType === 'TypeError' &&
        (errorMsg === 'Failed to fetch' ||
          errorMsg === 'NetworkError when attempting to fetch resource.' ||
          errorMsg.startsWith('cancelled') ||
          errorMsg.startsWith('The Internet connection appears to be offline'))
      ) {
        return null
      }
      // Drop the 401 re-authenticate event — operational, the auth flow
      // already handles it. (OOE-UI-9Q)
      //
      // NOTE: we deliberately do NOT filter "Token not valid for this
      // account" (403 from ooe-service cc-order-route). That message
      // signals a real scope mismatch (see FULSAS-9162) and must remain
      // visible in Sentry so genuine regressions surface.
      if (errorMsg === 'Please re-authenticate.') {
        return null
      }
      return event
    },
    // Drop Sentry performance issues we cannot resolve from app code (e.g.
    // "Uncompressed Asset" alerts on /payment-information/* — that requires
    // gzip/brotli at the nginx/CDN layer). (OOE-UI-5H)
    beforeSendTransaction(event) {
      const issueType =
        event?.contexts?.trace?.data?.['sentry.issue_type'] ||
        event?.contexts?.performance_issue?.type ||
        ''
      if (
        typeof issueType === 'string' &&
        issueType.includes('performance_uncompressed_assets')
      ) {
        return null
      }
      return event
    },

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  })

  function FullPageErrorFallback({error}) {
    const handleClick = () => window.location.reload()

    return (
      <ScreenContainer>
        <ErrorContainer>
          <ItemContainer>
            <TextContainer>
              <ParagraphText
                variant="reg"
                style={{
                  color: '#ff5101',
                  fontWeight: 'bold',
                  fontSize: '2rem',
                  marginBottom: '20px',
                }}
              >
                Uh Oh!
              </ParagraphText>
              <ParagraphText
                variant="reg"
                className="m-0"
                style={{fontWeight: 'bold', fontSize: '1rem'}}
              >
                There&apos;s a problem. Try refreshing the app.
              </ParagraphText>
            </TextContainer>
            <Button
              onClick={handleClick}
              variant="primary"
              style={{marginTop: '50px'}}
            >
              Refresh Now
            </Button>
          </ItemContainer>
          <ImageContainer>
            <img src={thryvGuyConfused} alt="confused Thryv Guy" />
          </ImageContainer>
        </ErrorContainer>
      </ScreenContainer>
    )
  }

  ReactDOM.render(
    <LDProvider>
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={FullPageErrorFallback}>
          <Provider store={Store}>
            <AppProviders>
              <App />
            </AppProviders>
          </Provider>
        </Sentry.ErrorBoundary>
      </React.StrictMode>
    </LDProvider>,
    document.getElementById('root'),
  )

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals()
})()
